import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { SavedCard } from '../types';

// Stripe.js is loaded on demand from Stripe's CDN so the card details are
// tokenized in the browser and the raw PAN never reaches our servers.
declare global {
  interface Window {
    Stripe?: (key: string) => any;
  }
}

const STRIPE_JS = 'https://js.stripe.com/v3';

function injectStripeJs(): Promise<void> {
  return new Promise((resolve, reject) => {
    // Drop any previously-failed tag so we always retry with a fresh one.
    document.querySelectorAll(`script[data-stripe-js="1"]`).forEach((el) => el.remove());
    const s = document.createElement('script');
    s.src = STRIPE_JS + (STRIPE_JS.includes('?') ? '&' : '?') + 'cb=' + Date.now();
    s.async = true;
    s.setAttribute('data-stripe-js', '1');
    s.onload = () => (window.Stripe ? resolve() : reject(new Error('Stripe.js loaded but window.Stripe missing')));
    s.onerror = () => reject(new Error('Failed to load Stripe.js (blocked or offline)'));
    document.body.appendChild(s);
  });
}

function loadStripeJs(): Promise<void> {
  return new Promise(async (resolve, reject) => {
    // 1. Already available (preloaded in index.html).
    if (window.Stripe) return resolve();
    // 2. A tag is present (e.g. the index.html preload still loading) — poll briefly.
    const preloaded = document.querySelector(`script[src^="${STRIPE_JS}"]`);
    if (preloaded) {
      for (let i = 0; i < 60; i++) {           // up to ~6s
        if (window.Stripe) return resolve();
        await new Promise((r) => setTimeout(r, 100));
      }
    }
    // 3. Inject fresh (with retry via cache-buster) and wait.
    try {
      await injectStripeJs();
      resolve();
    } catch (e) {
      reject(e as Error);
    }
  });
}

interface AddCardModalProps {
  open: boolean;
  accountId: number | null;
  accountLabel?: string;
  onClose: () => void;
  onAdded?: (cards: SavedCard[]) => void;
}

export const AddCardModal: React.FC<AddCardModalProps> = ({ open, accountId, accountLabel, onClose, onAdded }) => {
  const { showSuccess } = useToast();
  const cardElRef = useRef<HTMLDivElement>(null);
  const stripeRef = useRef<any>(null);
  const cardRef = useRef<any>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !accountId) return;
    let cancelled = false;
    setError(null);
    setReady(false);
    setLoading(true);

    (async () => {
      try {
        console.log('[ADD_CARD] card-setup: fetching publishable key', { accountId });
        const setup = await api.getDiceCardSetup(accountId);
        console.log('[ADD_CARD] card-setup response', { success: setup.success, hasKey: !!setup.publishable_key, error: setup.error });
        if (!setup.success || !setup.publishable_key) {
          throw new Error(setup.error || 'Could not start card setup');
        }
        await loadStripeJs();
        console.log('[ADD_CARD] Stripe.js loaded', { stripeAvailable: !!window.Stripe });
        if (cancelled || !window.Stripe) return;

        const stripe = window.Stripe(setup.publishable_key);
        stripeRef.current = stripe;
        const elements = stripe.elements();
        const card = elements.create('card', {
          style: {
            base: {
              color: '#ffffff',
              fontSize: '16px',
              '::placeholder': { color: 'rgba(255,255,255,0.4)' },
            },
            invalid: { color: '#f87171' },
          },
        });
        cardRef.current = card;
        // The container div is always rendered (see JSX), so it exists now — mount
        // straight into it. Enable the button only once the element is interactive.
        if (cardElRef.current) {
          card.mount(cardElRef.current);
          console.log('[ADD_CARD] card element mounted');
        } else {
          console.warn('[ADD_CARD] mount target missing at mount time');
        }
        card.on('ready', () => { if (!cancelled) { setReady(true); console.log('[ADD_CARD] card element ready'); } });
        card.on('change', (ev: any) => { if (!cancelled) setError(ev?.error?.message || null); });
        setLoading(false);
      } catch (e: any) {
        console.error('[ADD_CARD] card-setup init failed', e);
        if (!cancelled) {
          setError(e?.message || 'Failed to initialize card form');
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      try { cardRef.current?.destroy(); } catch { /* noop */ }
      cardRef.current = null;
    };
  }, [open, accountId]);

  const handleSubmit = async () => {
    if (!stripeRef.current || !cardRef.current || !accountId) return;
    setSubmitting(true);
    setError(null);
    try {
      console.log('[ADD_CARD] tokenizing card with Stripe.js…', { accountId });
      const { paymentMethod, error: pmErr } = await stripeRef.current.createPaymentMethod({
        type: 'card',
        card: cardRef.current,
      });
      if (pmErr) {
        console.warn('[ADD_CARD] createPaymentMethod error', pmErr);
        throw new Error(pmErr.message);
      }
      console.log('[ADD_CARD] tokenized ok', { pm: paymentMethod?.id, brand: paymentMethod?.card?.brand, last4: paymentMethod?.card?.last4 });
      const res = await api.addDiceCard(accountId, paymentMethod.id);
      console.log('[ADD_CARD] add-card response', { success: res.success, cards: res.saved_cards?.length, error: res.error });
      if (!res.success) throw new Error(res.error || 'Failed to add card');
      console.log('[ADD_CARD] SUCCESS card added', { accountId, pm: paymentMethod.id });
      showSuccess('Card added');
      onAdded?.(res.saved_cards || []);
      onClose();
    } catch (e: any) {
      console.error('[ADD_CARD] add-card failed', e);
      setError(e?.message || 'Failed to add card');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-2xl bg-[#141414] border border-white/10 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-medium text-white">
          Add card{accountLabel ? ` · ${accountLabel}` : ''}
        </h3>
        <p className="text-sm text-white/50 mt-1">
          Your card is sent directly to Stripe — it never touches our servers.
        </p>

        <div className="mt-4 rounded-xl bg-white/5 border border-white/10 px-3 py-3 min-h-[48px] flex items-center relative">
          {/* Always rendered so the Stripe element has a mount target. */}
          <div ref={cardElRef} className="w-full" />
          {loading && (
            <span className="absolute inset-0 flex items-center px-3 text-white/40 text-sm bg-[#141414] rounded-xl">
              Loading secure card form…
            </span>
          )}
        </div>

        {error && <p className="text-sm text-red-400 mt-2">{error}</p>}

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg bg-white/5 text-white/60 hover:bg-white/10 transition-all disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!ready || submitting}
            className="px-4 py-2 rounded-lg bg-emerald-500/90 text-white hover:bg-emerald-500 transition-all disabled:opacity-50"
          >
            {submitting ? 'Adding…' : 'Add card'}
          </button>
        </div>
      </div>
    </div>
  );
};
